/**
 * VirusTotal investigation collector.
 *
 * Queries VirusTotal for domain/IP/hash reputation data suitable for
 * entity extraction during investigations.
 */

import { getApiBase } from '@/app/api-router';
import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 15_000;

const IP_REGEX = /^(\d{1,3}\.){3}\d{1,3}$/;
const HASH_REGEX = /^[a-f0-9]{32,64}$/i;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VTDetectionStats {
  malicious: number;
  suspicious: number;
  undetected: number;
  harmless: number;
}

export interface VTCollectionResult {
  rawText: string;
  detectionStats?: VTDetectionStats;
  reputation?: number;
  tags: string[];
  queryTarget: string;
  queryType: 'domain' | 'ip' | 'file';
}

// ---------------------------------------------------------------------------
// Target type detection
// ---------------------------------------------------------------------------

function detectTargetType(target: string): 'domain' | 'ip' | 'file' {
  if (IP_REGEX.test(target)) return 'ip';
  if (HASH_REGEX.test(target)) return 'file';
  return 'domain';
}

// ---------------------------------------------------------------------------
// Raw text builder
// ---------------------------------------------------------------------------

interface VTAttributes {
  last_analysis_stats?: {
    malicious?: number;
    suspicious?: number;
    undetected?: number;
    harmless?: number;
  };
  reputation?: number;
  tags?: string[];
  as_owner?: string;
  country?: string;
  last_analysis_date?: number;
  total_votes?: { malicious?: number; harmless?: number };
  registrar?: string;
  creation_date?: number;
  whois?: string;
}

function buildRawText(
  target: string,
  type: 'domain' | 'ip' | 'file',
  attrs: VTAttributes,
): string {
  const lines: string[] = [];
  const stats = attrs.last_analysis_stats;

  lines.push(`VirusTotal Report: ${target} (${type})`);
  lines.push('');

  if (stats) {
    const total =
      (stats.malicious ?? 0) +
      (stats.suspicious ?? 0) +
      (stats.undetected ?? 0) +
      (stats.harmless ?? 0);
    lines.push(`Detection: ${stats.malicious ?? 0} malicious, ${stats.suspicious ?? 0} suspicious out of ${total} engines`);
  }

  if (attrs.reputation != null) {
    lines.push(`Reputation score: ${attrs.reputation}`);
  }

  if (attrs.as_owner) lines.push(`AS Owner: ${attrs.as_owner}`);
  if (attrs.country) lines.push(`Country: ${attrs.country}`);
  if (attrs.registrar) lines.push(`Registrar: ${attrs.registrar}`);

  if (attrs.total_votes) {
    lines.push(
      `Community votes: ${attrs.total_votes.malicious ?? 0} malicious, ${attrs.total_votes.harmless ?? 0} harmless`,
    );
  }

  if (attrs.tags && attrs.tags.length > 0) {
    lines.push(`Tags: ${attrs.tags.join(', ')}`);
  }

  if (attrs.last_analysis_date) {
    lines.push(`Last analysis: ${new Date(attrs.last_analysis_date * 1000).toISOString()}`);
  }

  if (attrs.creation_date) {
    lines.push(`Created: ${new Date(attrs.creation_date * 1000).toISOString()}`);
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

export async function collectFromVirusTotal(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<VTCollectionResult> {
  const query = task.query as Record<string, unknown>;

  // Extract target: prefer enrichmentTarget, fall back to first keyword
  const target =
    (query.enrichmentTarget as string) ??
    ((query.keywords as string[] | undefined)?.[0] as string | undefined) ??
    '';

  if (!target) {
    return {
      rawText: 'No target provided for VirusTotal lookup.',
      tags: [],
      queryTarget: '',
      queryType: 'domain',
    };
  }

  const type = detectTargetType(target);

  const url = `${getApiBase()}/api/data/virustotal?type=${encodeURIComponent(type)}&target=${encodeURIComponent(target)}`;

  const response = await globalThis.fetch(url, {
    headers: {
      'User-Agent': 'Sentinel/1.0 OSINT Platform',
      Accept: 'application/json',
    },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    throw new Error(`VirusTotal API returned ${response.status}: ${response.statusText}`);
  }

  const data = (await response.json()) as {
    data?: { attributes?: VTAttributes };
  };

  const attrs = data.data?.attributes ?? {};
  const stats = attrs.last_analysis_stats;

  const detectionStats: VTDetectionStats | undefined = stats
    ? {
        malicious: stats.malicious ?? 0,
        suspicious: stats.suspicious ?? 0,
        undetected: stats.undetected ?? 0,
        harmless: stats.harmless ?? 0,
      }
    : undefined;

  return {
    rawText: buildRawText(target, type, attrs),
    detectionStats,
    reputation: attrs.reputation,
    tags: attrs.tags ?? [],
    queryTarget: target,
    queryType: type,
  };
}
